import {Component, OnDestroy, OnInit} from '@angular/core';
import {LoaderComponent} from "../../components/ui/loader/loader.component";
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {ProjectPhotoService} from "../../services/project-photo.service";
import {BehaviorSubject, combineLatest, firstValueFrom, map, Subscription, switchMap, tap} from "rxjs";
import {Project} from "../../models/project";
import {ActivatedRoute} from "@angular/router";
import {SharedModule} from "../../shared/shared.module";
import {ProjectPhotoPreviewPipe} from "../../pipes/project-photo-preview.pipe";
import {ModalService} from "../../services/modal.service";
import {ProjectPhoto} from "../../models/project-photo";
import {
  ProjectPhotoViewModalComponent
} from "../../components/modals/project-photo-view-modal/project-photo-view-modal.component";
import {DeleteConfirmModalComponent} from "../../components/modals/delete-confirm-modal/delete-confirm-modal.component";
import {ButtonIconComponent} from "../../components/ui/button-icon/button-icon.component";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {CheckboxComponent} from "../../components/ui/checkbox/checkbox.component";
import {SelectComponent} from "../../components/ui/select/select.component";
import {ButtonComponent} from "../../components/ui/button/button.component";

enum DownloadType {
  DOWNLOAD_ZIP = 'DOWNLOAD_ZIP',
}

@Component({
  selector: 'app-project-detail-photos',
  standalone: true,
  imports: [
    LoaderComponent,
    NgIf,
    AsyncPipe,
    SharedModule,
    ProjectPhotoPreviewPipe,
    NgForOf,
    ButtonIconComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    SelectComponent,
    ButtonComponent
  ],
  templateUrl: './project-detail-photos.component.html',
  styleUrl: './project-detail-photos.component.scss'
})
export class ProjectDetailPhotosComponent implements OnInit, OnDestroy {
  loading = true;
  refresh$ = new BehaviorSubject(null);
  public project$ = this.route.parent!.data.pipe(map(data => data['project'] as Project));
  public selectionFormControls = new Map<number, FormControl>();
  photos$ = combineLatest([this.project$, this.refresh$])
    .pipe(
      switchMap(([project]) => this.projectPhotoService.list(project)),
      tap(() => this.loading = false),
    );
  public selection: number[] = [];
  downloadControl = new FormControl(null as DownloadType | null);
  downloading = false;
  downloadOptions = [
    { value: null, labelKey: 'page.project-detail-photos.selected.placeholder' },
    { value: DownloadType.DOWNLOAD_ZIP, labelKey: 'page.project-detail-photos.selected.download-zip' },
  ];

  subscriptions: Subscription[] = [];
  allSelected = false;

  constructor(private projectPhotoService: ProjectPhotoService, private route: ActivatedRoute, private modalService: ModalService) {
  }

  ngOnInit() {
    this.subscriptions.push(
      this.photos$.subscribe((photos) => {
        this.createSelectionFormControls(photos);
      }),
      this.downloadControl.valueChanges.subscribe(value => {
        if (value) {
          this.performDownload(value);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  async openPhoto(photo: ProjectPhoto) {
    this.modalService.open(ProjectPhotoViewModalComponent, {
      data: {
        projectPhoto: photo,
      }
    });
  }

  deleteProjectPhoto(projectPhoto: ProjectPhoto) {
    this.modalService.open(DeleteConfirmModalComponent, {
      data: {
        type: 'page.project-detail-photos.delete-photos.entity',
        name: `#${projectPhoto.id}`,
        confirm: async () => {
          await firstValueFrom(this.projectPhotoService.delete(projectPhoto));
          this.refresh$.next(null);
        },
      }
    });
  }

  createSelectionFormControls(photos: ProjectPhoto[]) {
    // Create a form control for each item in the list
    this.selectionFormControls.clear();

    for (const item of photos) {
      if (!this.selectionFormControls.has(item.id)) {
        const control = new FormControl(false);
        this.selectionFormControls.set(item.id, control);

        this.subscriptions.push(
          control.valueChanges.subscribe(() => {
            this.checkSelection();
          }),
        );
      }
    }
  }

  checkSelection() {
    this.selection = Array.from(this.selectionFormControls.entries())
      .filter(([_, control]) => control.value)
      .map(([id, _]) => id);

    this.allSelected = this.selection.length === this.selectionFormControls.size;
  }

  getSelectionControlForItem(item: ProjectPhoto): FormControl {
    return this.selectionFormControls.get(item.id)!;
  }

  async performDownload(type: DownloadType) {
    if (this.downloading) {
      return;
    }

    const project = await firstValueFrom(this.project$);

    try {
      this.downloading = true;
      const blob = await firstValueFrom(this.projectPhotoService.downloadMultiple(project, this.selection));

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `project-${project.id}-photos.zip`;

      a.click();
      window.URL.revokeObjectURL(url);

      this.downloading = false;

      this.downloadControl.setValue(null);
    } catch (e) {
      console.error(e);
    }
  }

  toggleAllSelected() {
    if (this.allSelected) {
      this.selectionFormControls.forEach(control => control.setValue(false));
    } else {
      this.selectionFormControls.forEach(control => control.setValue(true));
    }

    this.checkSelection();
  }
}
